import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Drawer } from '@mui/material';
import { styled } from '@mui/material/styles';

import HalfSheet from '../../components/HalfSheet';
import Confetti from '../../components/Confetti';
import Icon from '../../components/Icon';
import EventMap from './EventMap';
import MarkdownText from '../../components/MarkdownText';
import { CALLOUT_W, SIDEBAR_GROUND } from '../../components/Frame';
import { notchMask } from '../../components/notch';
import type { EditScope } from '../edit/eventEdits';
import { TAG_COLORS } from '../../shared/icons.generated';
import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import { COMMITMENT_LABEL, formatArea, formatTimeRange, longDate, timeSlotOf } from '../../shared/taxonomy';
import { sampleAttendanceText } from '../../shared/sample';
import type { RsvpStatus } from '../../shared/useRsvp';
import { canGo, entryWord, shortDate } from './cardFacts';
import { SampleMark } from './sampleMark';
import {
  eventUrl,
  formatLabel,
  hasEndTime,
  localDate,
  venueText,
  type Opportunity,
} from '../../shared/opportunity';

/**
 * The panel a card click opens.
 *
 * This is the answer to the "whole card is a link" problem. Rather than
 * throwing the user out to a third-party page for every event they merely want
 * to consider — which is the tab explosion their own planning research
 * describes — the card opens here. Everything needed to triage is on screen,
 * and exactly one control leaves the site.
 *
 * Drawn as a parchment ticket on the sidebar ground, per the Sept 17
 * references ("09.17 REFERENCE event callout …"): the ticket carries what the
 * event is, and WHEN and WHERE below it, on the ground, carry how it fits the
 * user's day.
 */

/** How long the right sidebar takes to fade from the callout back to its panel: `RightColumn`'s, and a little. */
const PANE_FADE_MS = 400;

/** The palette's single red, reserved for "this will not work". */
const CONFLICT = '#ef3d23';
const INK = BRAND.mocha;
const INK_SOFT = 'rgba(54,47,42,0.55)';

/**
 * Every length here is a share of the ticket's width, as measured off the
 * references, so the one drawing holds at a phone's 330px ticket and the
 * desktop's 400px alike. The column is a size container; the ticket is its
 * width less the ground either side.
 */
const GROUND_SIDE = 6.2;
const TICKET_SHARE = 1 - (2 * GROUND_SIDE) / 100;
/** A share of the ticket's width, as a length. */
const tw = (share: number) => `${+(share * TICKET_SHARE * 100).toFixed(3)}cqw`;
/** The notch radius, and the ticket's content inset: text starts where the notch curve meets the edge. */
const INSET = tw(0.05);
/** How far a tag's icon chip hangs past the ticket's right edge. */
const CHIP = 0.058;

const Sheet = styled('div', { shouldForwardProp: (p) => p !== '$layout' })<{ $layout: CalloutLayout }>(
  ({ $layout }) => ({
    width: CALLOUT_W,
    maxWidth: '100vw',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    background: SIDEBAR_GROUND,
    color: BRAND.parchment,
    // The right sidebar: its width is its own, whatever the list wants.
    ...($layout === 'pane' && { flexShrink: 0 }),
    // Inside a HalfSheet, which sets the size.
    ...(($layout === 'half' || $layout === 'sheet') && {
      width: '100%',
      maxWidth: 'none',
      flex: 1,
      minHeight: 0,
    }),
  }),
);

const Scroll = styled('div')({
  position: 'relative',
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  overscrollBehavior: 'contain',
});

/**
 * The width everything is measured against. Capped, so the half-up callout
 * across a 760px screen draws a ticket, not a banner.
 */
const Column = styled('div')({
  containerType: 'inline-size',
  width: '100%',
  maxWidth: 480,
  margin: '0 auto',
});

const Ground = styled('div')({
  padding: `0 ${GROUND_SIDE}cqw`,
});

// --- the ticket ------------------------------------------------------------

/**
 * Its date and controls. The top of the ticket, and held there as the rest
 * scrolls: close, edit and back stay one reach away at every depth.
 */
const Head = styled('div')({
  position: 'sticky',
  top: 0,
  zIndex: 3,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: tw(0.02),
  padding: `${tw(0.056)} ${INSET} ${tw(0.024)}`,
  background: BRAND.parchment,
  color: INK,
});

const DateLine = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: tw(0.02),
  minWidth: 0,
  fontFamily: FONTS.body,
  fontSize: tw(0.0259),
  fontWeight: 700,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  color: INK_SOFT,
  whiteSpace: 'nowrap',
  '& > span:first-of-type': { overflow: 'hidden', textOverflow: 'ellipsis' },
});

/**
 * Said where the date is, because the date is what makes it true. Only ever
 * seen from My Schedule: Explore does not list events that are over.
 */
const PastTag = styled('span')({
  flexShrink: 0,
  fontSize: tw(0.021),
  letterSpacing: '0.1em',
  lineHeight: 1,
  padding: `${tw(0.009)} ${tw(0.017)}`,
  borderRadius: 999,
  color: INK,
  background: 'rgba(54,47,42,0.12)',
});

const HeadControls = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: tw(0.008),
  flexShrink: 0,
});

const EditPill = styled('button')({
  appearance: 'none',
  cursor: 'pointer',
  height: tw(0.058),
  padding: `0 ${tw(0.026)}`,
  marginRight: tw(0.012),
  borderRadius: 999,
  border: '1.5px solid rgba(54,47,42,0.55)',
  background: 'transparent',
  color: INK,
  fontFamily: FONTS.body,
  fontSize: tw(0.024),
  fontWeight: 700,
  whiteSpace: 'nowrap',
  '&:hover': { background: 'rgba(54,47,42,0.06)', borderColor: INK },
});

const HeadButton = styled('button')({
  appearance: 'none',
  width: tw(0.066),
  height: tw(0.066),
  padding: 0,
  display: 'grid',
  placeItems: 'center',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: INK,
  '& svg': { width: '62%', height: '62%' },
  '&:hover:not(:disabled)': { opacity: 0.7 },
  '&:disabled': { color: 'rgba(54,47,42,0.35)', cursor: 'default' },
});

/** The paper of one half, as a masked layer beneath its contents, so a tag can hang past the edge. */
const paper = (corners: ('tl' | 'tr' | 'bl' | 'br')[]) => ({
  position: 'relative' as const,
  isolation: 'isolate' as const,
  color: INK,
  '&::before': {
    content: '""',
    position: 'absolute' as const,
    inset: 0,
    zIndex: -1,
    background: BRAND.parchment,
    ...notchMask({ radius: INSET, corners }),
  },
});

/** Everything above the perforation. Its top runs off the column, so only its foot is notched. */
const Body = styled('div')({
  ...paper(['bl', 'br']),
  // Holds the title's margin inside, where it is paper, rather than letting it
  // collapse through to show the ground between the header and the body.
  display: 'flow-root',
  padding: `0 ${INSET} ${tw(0.081)}`,
});

/** Says the listing on screen is the user's version, with the way back. */
const EditedNote = styled('p')({
  margin: `${tw(0.01)} 0 0`,
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'baseline',
  gap: `${tw(0.006)} ${tw(0.02)}`,
  fontFamily: FONTS.body,
  fontSize: tw(0.026),
  color: 'rgba(54,47,42,0.7)',
});

const TextButton = styled('button')({
  appearance: 'none',
  background: 'none',
  border: 'none',
  padding: 0,
  cursor: 'pointer',
  fontFamily: FONTS.body,
  fontSize: 'inherit',
  fontWeight: 700,
  color: 'inherit',
  textDecoration: 'underline',
  textUnderlineOffset: 3,
  '&:hover': { opacity: 0.75 },
});

const Title = styled('h2')({
  margin: `${tw(0.02)} 0 0`,
  fontFamily: FONTS.serif,
  fontWeight: 700,
  fontSize: tw(0.051),
  lineHeight: tw(0.0636),
  letterSpacing: '-0.01em',
  textWrap: 'pretty',
  overflowWrap: 'anywhere',
});

/** The hours and the place on the left, the tags hung from the right edge. */
const Facts = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto',
  alignItems: 'start',
  columnGap: tw(0.03),
  marginTop: tw(0.015),
});

const Fields = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: tw(0.029),
  minWidth: 0,
});

/**
 * The hours or the place, with the warning that qualifies it. The whole of it,
 * warning included, takes the reader to the section below that explains it.
 */
const Field = styled('button')({
  appearance: 'none',
  display: 'block',
  width: '100%',
  padding: 0,
  margin: 0,
  background: 'none',
  border: 'none',
  textAlign: 'left',
  font: 'inherit',
  color: 'inherit',
  cursor: 'pointer',
  '&:hover .field-line': { textDecoration: 'underline', textUnderlineOffset: 3 },
});

const FieldLine = styled('span')({
  display: 'flex',
  alignItems: 'center',
  gap: tw(0.02),
  minHeight: tw(0.062),
  fontFamily: FONTS.body,
  fontSize: tw(0.0303),
  fontWeight: 700,
  lineHeight: 1.15,
});

const FieldIcon = styled('span')({
  display: 'grid',
  placeItems: 'center',
  width: tw(0.035),
  height: tw(0.035),
  flexShrink: 0,
});

const EndNote = styled('span')({
  fontSize: tw(0.022),
  fontWeight: 600,
  color: INK_SOFT,
  whiteSpace: 'nowrap',
});

const Tags = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: tw(0.027),
  // Out through the inset to the ticket's edge, and past it by the chip.
  marginRight: tw(-(0.05 + CHIP)),
});

/**
 * Tabs shown before the rest wait behind "more". The references draw four;
 * some listings carry seventeen, and a column of them would run past the
 * description to the stub.
 */
const TAG_LIMIT = 4;

const MoreTags = styled('button')({
  appearance: 'none',
  alignSelf: 'flex-end',
  marginRight: tw(0.074),
  padding: 0,
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontFamily: FONTS.body,
  fontSize: tw(0.0207),
  fontWeight: 700,
  color: INK_SOFT,
  '&:hover': { color: INK, textDecoration: 'underline', textUnderlineOffset: 2 },
});

const TagTab = styled('div')({
  display: 'flex',
  width: tw(0.416),
  height: tw(0.062),
});

const TagLabel = styled('span')({
  flex: 1,
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: `0 ${tw(0.016)} 0 ${tw(0.02)}`,
  borderRadius: `${tw(0.012)} 0 0 ${tw(0.012)}`,
  fontFamily: FONTS.body,
  fontSize: tw(0.0207),
  fontWeight: 700,
  color: INK,
  '& > span': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
});

const TagChip = styled('span')({
  width: tw(CHIP),
  flexShrink: 0,
  display: 'grid',
  placeItems: 'center',
  borderRadius: `0 ${tw(0.012)} ${tw(0.012)} 0`,
  color: INK,
  '& > span': { width: tw(0.042), height: tw(0.042) },
});

/** The long form, on the paper. */
const Description = styled(MarkdownText)({
  marginTop: tw(0.04),
  fontSize: `max(10.8px, ${tw(0.0281)})`,
  lineHeight: 1.2,
  color: 'rgba(54,47,42,0.85)',
  '& p, & ul, & ol': { marginBottom: tw(0.02) },
  '& strong': { color: INK },
  '& li::marker': { color: 'rgba(54,47,42,0.45)' },
  '& a': { textDecorationColor: 'rgba(54,47,42,0.4)' },
  '& a:hover': { textDecorationColor: INK },
});

/**
 * The stub torn off: dropped and turned as `event callout broken stub.jpg`
 * draws it. Measured there, its centre falls 10% of the ticket's width and
 * shifts 3.7% left, and it turns 8° clockwise; the spec's round "about 9%
 * down" is the same drop taken at a corner.
 */
const TORN = `translate(${tw(-0.037)}, ${tw(0.1)}) rotate(8deg)`;
/** The tear snaps and settles; putting it back is quieter. */
const TEAR = 'transform 650ms cubic-bezier(0.3, 1.35, 0.55, 1)';
const MEND = 'transform 420ms cubic-bezier(0.4, 0, 0.2, 1)';

/**
 * Below the perforation. Split exactly down the middle, per the reference.
 * Torn off at the perforation while the event is on the schedule (Phase 7):
 * adding it tears it, and it opens torn from then on until it is removed.
 */
const Stub = styled('div', { shouldForwardProp: (p) => p !== '$torn' && p !== '$live' })<{
  $torn: boolean;
  /** Seen once already: a change now is the user's, and moves. */
  $live: boolean;
}>(({ $torn, $live }) => ({
  ...paper(['tl', 'tr', 'bl', 'br']),
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  minHeight: tw(0.348),
  transform: $torn ? TORN : 'none',
  transition: $live ? ($torn ? TEAR : MEND) : 'none',
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  '&::after': {
    content: '""',
    position: 'absolute',
    left: '50%',
    top: tw(0.037),
    bottom: tw(0.03),
    borderLeft: '1px solid rgba(54,47,42,0.75)',
  },
}));

/**
 * The dots between the two halves, punched through to the ground. Drawn on
 * both edges of the seam, where they coincide: torn apart, each keeps its half
 * of every hole.
 */
const Perforation = styled('div', { shouldForwardProp: (p) => p !== '$edge' })<{ $edge: 'top' | 'bottom' }>(
  ({ $edge }) => ({
  position: 'absolute',
  [$edge]: tw(-0.0055),
  left: tw(0.0755),
  right: tw(0.0755),
  display: 'flex',
  justifyContent: 'space-between',
  zIndex: 1,
  '& > span': {
    width: tw(0.011),
    height: tw(0.011),
    borderRadius: '50%',
    background: SIDEBAR_GROUND,
  },
  }),
);

/** Where the tear's confetti starts: the middle of the seam. */
const TearPoint = styled('span')({
  position: 'absolute',
  left: 0,
  right: 0,
  top: 0,
  height: 0,
  zIndex: 2,
});

const Holes = () => (
  <>
    {Array.from({ length: 24 }, (_, i) => (
      <span key={i} />
    ))}
  </>
);

const StubHalf = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  padding: `${tw(0.064)} ${tw(0.03)} ${tw(0.04)}`,
});

const Intents = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  gap: tw(0.02),
  // The card's intent grey, per the design spec.
  color: '#c9c5c1',
  '& > span': { display: 'block', width: tw(0.165), height: tw(0.165) },
  '&[data-two="true"] > span': { width: tw(0.155), height: tw(0.155) },
});

const TypeWord = styled('div')({
  marginTop: tw(0.014),
  fontFamily: FONTS.serif,
  fontWeight: 700,
  fontSize: tw(0.0394),
  lineHeight: tw(0.05),
  letterSpacing: '-0.005em',
});

const StubLabel = styled('div')({
  fontFamily: FONTS.body,
  fontSize: tw(0.0184),
  letterSpacing: '0.04em',
  lineHeight: tw(0.024),
  color: 'rgba(54,47,42,0.6)',
  '& ~ &': { marginTop: tw(0.037) },
});

const StubValue = styled('div')({
  marginTop: tw(0.014),
  fontFamily: FONTS.serif,
  fontWeight: 700,
  fontSize: tw(0.0391),
  lineHeight: tw(0.05),
});

// --- on the ground -----------------------------------------------------------

const Rule = styled('hr')({
  margin: 0,
  border: 0,
  borderTop: '1px solid rgba(243,238,231,0.35)',
});

const Section = styled('section')({
  paddingTop: tw(0.075),
});

const SectionLabel = styled('div')({
  margin: `0 ${INSET}`,
  fontFamily: FONTS.body,
  fontSize: tw(0.0227),
  fontWeight: 700,
  letterSpacing: '0.06em',
  lineHeight: tw(0.03),
  textTransform: 'uppercase',
  color: 'rgba(243,238,231,0.55)',
});

const SectionHeading = styled('div')({
  display: 'flex',
  alignItems: 'baseline',
  flexWrap: 'wrap',
  columnGap: tw(0.047),
  margin: `${tw(0.014)} ${INSET} 0`,
  fontFamily: FONTS.serif,
  fontWeight: 700,
  fontSize: tw(0.0486),
  lineHeight: 1.2,
  letterSpacing: '-0.01em',
  '& > span': { fontWeight: 400, fontSize: tw(0.0399), letterSpacing: 0 },
});

const SectionNote = styled('p')({
  margin: `${tw(0.02)} ${INSET} 0`,
  fontFamily: FONTS.body,
  fontSize: tw(0.026),
  lineHeight: 1.35,
  color: 'rgba(243,238,231,0.6)',
});

const Address = styled('p')({
  margin: `${tw(0.01)} ${INSET} 0`,
  fontFamily: FONTS.body,
  fontSize: tw(0.028),
  lineHeight: 1.3,
  color: 'rgba(243,238,231,0.9)',
});

const MapSlot = styled('div')({
  margin: `${tw(0.07)} ${INSET} 0`,
});

const PlainActions = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: tw(0.012),
  padding: `${tw(0.075)} 0 ${tw(0.06)}`,
});

/** No pill, no border — just icon and label, per EVENT CALLOUT REF. */
const PlainAction = styled('button')({
  appearance: 'none',
  background: 'none',
  border: 'none',
  padding: `${tw(0.008)} ${tw(0.01)}`,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: tw(0.018),
  fontFamily: FONTS.body,
  fontSize: tw(0.0263),
  fontWeight: 600,
  color: 'rgba(243,238,231,0.9)',
  '& > span': { width: tw(0.032), height: tw(0.032) },
  '&:hover': { color: BRAND.parchment },
  '&[data-danger="true"]:hover': { color: CONFLICT },
  '&[aria-disabled="true"]': { opacity: 0.45, cursor: 'default' },
  '&[aria-disabled="true"]:hover': { color: 'rgba(243,238,231,0.9)' },
});

const PlainNote = styled('p')({
  margin: 0,
  fontFamily: FONTS.body,
  fontSize: tw(0.024),
  textAlign: 'center',
  color: 'rgba(243,238,231,0.55)',
});

// --- the footer ----------------------------------------------------------------

/** Outside the scrolling box: in reach at every scroll position. */
/** Under the title: where the listing came from. */
const SourceLine = styled('p')({
  margin: `${tw(0.012)} 0 0`,
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: tw(0.016),
  fontFamily: FONTS.body,
  fontSize: tw(0.027),
  fontWeight: 600,
  color: INK_SOFT,
});

/** Cancelled (red) or Full, beside the source. */
const StateChip = styled('span')({
  padding: `0 ${tw(0.016)}`,
  borderRadius: 999,
  fontSize: tw(0.022),
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  lineHeight: 1.6,
  color: BRAND.parchment,
  background: INK,
  '&[data-kind="cancelled"]': { background: CONFLICT },
});

/** "Orientation required first": a prerequisite, highlighted under WHEN. */
const Prereq = styled('p')({
  margin: `${tw(0.024)} ${INSET} 0`,
  padding: `${tw(0.014)} ${tw(0.024)}`,
  borderRadius: tw(0.016),
  background: 'rgba(246,217,168,0.16)',
  borderLeft: `3px solid #f6d9a8`,
  fontFamily: FONTS.body,
  fontSize: tw(0.027),
  fontWeight: 600,
  lineHeight: 1.35,
  color: '#f6d9a8',
});

/** A series' dates, each with its own Going and Maybe. */
const Dates = styled('ul')({
  listStyle: 'none',
  margin: `${tw(0.03)} ${INSET} 0`,
  padding: 0,
  display: 'grid',
  gap: tw(0.01),
});

const DateRow = styled('li')({
  display: 'flex',
  alignItems: 'center',
  gap: tw(0.016),
  padding: `${tw(0.008)} 0`,
  borderBottom: '1px solid rgba(243,238,231,0.12)',
  fontFamily: FONTS.body,
  fontSize: tw(0.027),
  '&[data-current="true"] > button:first-of-type': { fontWeight: 700, color: BRAND.parchment },
});

const DateLink = styled('button')({
  appearance: 'none',
  background: 'none',
  border: 'none',
  padding: 0,
  flex: 1,
  minWidth: 0,
  textAlign: 'left',
  cursor: 'pointer',
  font: 'inherit',
  color: 'rgba(243,238,231,0.8)',
  '&:hover': { textDecoration: 'underline', textUnderlineOffset: 3 },
});

const DateToggle = styled('button')({
  appearance: 'none',
  cursor: 'pointer',
  padding: `${tw(0.004)} ${tw(0.022)}`,
  borderRadius: 999,
  border: `1px solid rgba(243,238,231,0.5)`,
  background: 'transparent',
  font: 'inherit',
  fontSize: tw(0.024),
  fontWeight: 700,
  color: BRAND.parchment,
  '&[aria-pressed="true"]': { background: BRAND.parchment, color: INK, borderColor: BRAND.parchment },
  '&:disabled': { opacity: 0.35, cursor: 'default' },
});

/** Above the buttons: what the user has said. */
const RsvpLine = styled('p')({
  margin: `${tw(0.03)} 0 0`,
  textAlign: 'center',
  fontFamily: FONTS.serif,
  fontSize: tw(0.034),
  fontWeight: 700,
  color: BRAND.parchment,
});

/** Under the buttons: who else is going. Placeholder, marked Sample. */
const Proof = styled('p')({
  margin: `${tw(0.022)} 0 0`,
  textAlign: 'center',
  fontFamily: FONTS.body,
  fontSize: tw(0.026),
  lineHeight: 1.4,
  color: 'rgba(243,238,231,0.8)',
});

/** Copy link, and Go to My Schedule once Going. */
const FootLinks = styled('div')({
  display: 'flex',
  justifyContent: 'center',
  flexWrap: 'wrap',
  columnGap: tw(0.05),
});

const Footer = styled('div')({
  flexShrink: 0,
  background: SIDEBAR_GROUND,
  // The sheet runs to the bottom of the screen, where a phone's toggle sits:
  // Register and the footnote lift above its band.
  paddingBottom: 'var(--toggle-band, 0px)',
});

const FootButtons = styled('div')({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: tw(0.033),
  marginTop: tw(0.05),
});

const FootButton = styled('button', { shouldForwardProp: (p) => p !== '$on' })<{ $on?: boolean }>(
  ({ $on }) => ({
    position: 'relative',
    appearance: 'none',
    cursor: 'pointer',
    height: tw(0.094),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tw(0.02),
    borderRadius: 999,
    border: `1.5px solid ${BRAND.parchment}`,
    background: $on ? BRAND.parchment : 'rgba(243,238,231,0.05)',
    color: $on ? INK : BRAND.parchment,
    fontFamily: FONTS.body,
    fontSize: tw(0.0298),
    fontWeight: 700,
    whiteSpace: 'nowrap',
    transition: 'background 120ms ease, color 120ms ease',
    '& > span:first-of-type': { width: tw(0.04), height: tw(0.04) },
    '&:hover': { background: $on ? BRAND.parchment : 'rgba(243,238,231,0.12)' },
  }),
);

const GoToSchedule = styled('button')({
  appearance: 'none',
  background: 'none',
  border: 'none',
  padding: `${tw(0.004)} ${tw(0.01)}`,
  margin: `${tw(0.018)} auto 0`,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: tw(0.012),
  fontFamily: FONTS.body,
  fontSize: tw(0.026),
  fontWeight: 700,
  color: BRAND.parchment,
  '& > span': { width: tw(0.026), height: tw(0.026) },
  '&:hover': { textDecoration: 'underline', textUnderlineOffset: 3 },
});

const Register = styled('a')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: tw(0.108),
  marginTop: tw(0.047),
  borderRadius: 999,
  background: BRAND.parchment,
  color: INK,
  fontFamily: FONTS.body,
  fontSize: tw(0.0334),
  fontWeight: 700,
  textDecoration: 'none',
  '&:hover': { background: '#fff' },
});

const FootNote = styled('p')({
  margin: 0,
  padding: `${tw(0.028)} 0 ${tw(0.05)}`,
  fontFamily: FONTS.body,
  fontSize: tw(0.0264),
  textAlign: 'center',
  color: 'rgba(243,238,231,0.5)',
});

/** Sized by the box around it, which is in container units; an SVG's own width attribute is not. */
function Glyph({ name, emoji }: { name: string; emoji?: string | null }) {
  return (
    <span style={{ display: 'grid', placeItems: 'center' }}>
      <Icon name={name} emoji={emoji} size="100%" />
    </span>
  );
}

/** The asset set has no link icon; a chain link, drawn in the Glyph's box. */
function LinkGlyph() {
  return (
    <span style={{ display: 'grid', placeItems: 'center' }}>
      <svg viewBox="0 0 24 24" width="100%" height="100%" aria-hidden="true" focusable="false">
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          d="M10 14a4.5 4.5 0 0 0 6.4 0l3-3a4.5 4.5 0 0 0-6.4-6.4l-1 1M14 10a4.5 4.5 0 0 0-6.4 0l-3 3a4.5 4.5 0 0 0 6.4 6.4l1-1"
        />
      </svg>
    </span>
  );
}

function Chevron({ back }: { back?: boolean }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path
        d={back ? 'M15 4 7 12l8 8' : 'm9 4 8 8-8 8'}
        fill="none"
        stroke="currentColor"
        strokeWidth={2.2}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Cross() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M4 4l16 16M20 4 4 20" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" />
    </svg>
  );
}

/** The ticket's own pin; the brand's icon set has no standalone location icon. */
function LocationPin() {
  return (
    <svg viewBox="0 0 24 24" width="100%" height="100%" aria-hidden="true" focusable="false">
      <path
        fill="currentColor"
        d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7Zm0 9.5A2.5 2.5 0 1 1 12 6.5a2.5 2.5 0 0 1 0 5Z"
      />
    </svg>
  );
}

/** "Manhattan - Midtown" as the WHERE heading says it. The ticket says the same in capitals. */
const areaName = (area: string) => area.replace(/\s*-\s*/, ' — ');

/**
 * How the callout is drawn, which the page decides:
 *
 * - `pane`: the right sidebar, from 900px up where it fits beside the list.
 *   The list stays live and undimmed, so another card opens into it.
 * - `over`: from the right, over the list, which is dimmed and closes it on a
 *   click. From 900px up where it does not fit beside the list, and at 600–899.
 * - `half`: half up over the day view below 900px. The day stays in view and
 *   live, and never dims. See HalfSheet.
 * - `sheet`: a phone's, over anything else: full height over a dimmed page,
 *   and swiped down to close.
 */
export type CalloutLayout = 'pane' | 'over' | 'half' | 'sheet';

interface Props {
  event: Opportunity | null;
  layout: CalloutLayout;
  /** The event has already ended. */
  past?: boolean;
  scheduled: boolean;
  saved: boolean;
  onClose: () => void;
  /** Walk back and forward through the events opened here. Disabled when absent. */
  onBack?: () => void;
  onForward?: () => void;
  onToggleSchedule: (id: string) => void;
  onToggleSave: (id: string) => void;
  onReport: (event: Opportunity) => void;
  /** Marked Not Interested. */
  hidden: boolean;
  /** The user's edits to this listing, and who they were for; null when unedited. */
  edited: EditScope | null;
  onToggleHidden: (id: string) => void;
  onEdit: (event: Opportunity) => void;
  onRevertEdit: (id: string) => void;
  onGoToSchedule: () => void;
  /** Swaps the sheet to another event. */
  onOpenEvent: (event: Opportunity) => void;
  /** Every upcoming date of this event's series, soonest first, itself included. */
  seriesDates?: Opportunity[];
  /** Going or Maybe for any event, for the series' Dates list. */
  statusOf?: (id: string) => RsvpStatus | null;
  /** Copies the event's shareable link (`/e/:id`). */
  onCopyLink?: (event: Opportunity) => void;
}

export default function EventDetailSheet({
  event: current,
  layout,
  past = false,
  scheduled,
  saved,
  onClose,
  onBack,
  onForward,
  onToggleSchedule,
  onToggleSave,
  onReport,
  hidden,
  edited,
  onToggleHidden,
  onEdit,
  onRevertEdit,
  onGoToSchedule,
  onOpenEvent,
  seriesDates = [],
  statusOf,
  onCopyLink,
}: Props) {
  const [burst, setBurst] = useState(0);
  /**
   * The event whose stub has been drawn once. Until it has, a torn stub is
   * simply torn: opening something already on the schedule does not replay the
   * tear, and nor does walking back to it.
   */
  const [live, setLive] = useState<string | null>(null);
  /** Which event's full set of tags is showing, if any. */
  const [allTagsFor, setAllTagsFor] = useState<string | null>(null);

  /**
   * A HalfSheet keeps its event on screen while it slides away, and the right
   * sidebar while it fades back into the panel (`RightColumn`).
   */
  const sliding = layout === 'half' || layout === 'sheet';
  const keeps = sliding || layout === 'pane';
  const [retained, setRetained] = useState<Opportunity | null>(null);
  if (keeps && current && current !== retained) setRetained(current);
  const event = current ?? (keeps ? retained : null);
  // The pane has no sheet to say when it has gone: let it go once the fade is over.
  useEffect(() => {
    if (layout !== 'pane' || current || !retained) return;
    const gone = window.setTimeout(() => setRetained(null), PANE_FADE_MS);
    return () => window.clearTimeout(gone);
  }, [layout, current, retained]);


  const scroll = useRef<HTMLDivElement | null>(null);
  const head = useRef<HTMLDivElement | null>(null);
  const when = useRef<HTMLElement | null>(null);
  const where = useRef<HTMLElement | null>(null);

  // Another event, from a card, the mini day or ‹ ›: start at its top, where its
  // title is. A HalfSheet does this itself. Not on closing, while it fades as it was.
  const shownId = current?.id;
  useLayoutEffect(() => {
    if (shownId && scroll.current) scroll.current.scrollTop = 0;
  }, [shownId]);

  // Drawn once, as it stands: from now on its stub moves when the schedule changes.
  useEffect(() => {
    const frame = requestAnimationFrame(() => setLive(shownId ?? null));
    return () => cancelAnimationFrame(frame);
  }, [shownId]);

  // The right sidebar is not a modal, so nothing else gives it Escape. A dialog
  // over the page (an edit, a report) takes its own Escape first.
  useEffect(() => {
    if (layout !== 'pane' || !current) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (document.querySelector('.MuiModal-root:not(.MuiModal-hidden)')) return;
      onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [layout, current, onClose]);

  /** Brings a section to the top of the callout, just under the held header. */
  const reveal = (section: HTMLElement | null) => {
    const box = scroll.current;
    if (!box || !section) return;
    const top =
      section.getBoundingClientRect().top -
      box.getBoundingClientRect().top +
      box.scrollTop -
      (head.current?.offsetHeight ?? 0);
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    box.scrollTo({ top, behavior: still ? 'auto' : 'smooth' });
  };

  const sheet = event && (
    <Sheet role="dialog" aria-label={event.name} $layout={layout}>
      <Scroll ref={scroll} data-callout-scroll>
        <Column>
          <Ground>
            <Head ref={head} data-callout-chrome data-half-anchor>
              <DateLine>
                <span>
                  {longDate(localDate(event)).weekday}, {longDate(localDate(event)).full}
                </span>
                {past && <PastTag>Past Event</PastTag>}
              </DateLine>
              <HeadControls>
                <EditPill type="button" onClick={() => onEdit(event)}>
                  Edit this event
                </EditPill>
                <HeadButton type="button" onClick={onBack} disabled={!onBack} aria-label="Back" title="Back">
                  <Chevron back />
                </HeadButton>
                <HeadButton
                  type="button"
                  onClick={onForward}
                  disabled={!onForward}
                  aria-label="Forward"
                  title="Forward"
                >
                  <Chevron />
                </HeadButton>
                <HeadButton type="button" onClick={onClose} aria-label="Close">
                  <Cross />
                </HeadButton>
              </HeadControls>
            </Head>

            <Body>
              {edited && (
                <EditedNote>
                  <span>
                    {edited === 'everyone'
                      ? 'You edited this listing and sent the changes for review.'
                      : 'You edited this listing for yourself.'}
                  </span>
                  <TextButton type="button" onClick={() => onRevertEdit(event.id)}>
                    Undo my edits
                  </TextButton>
                </EditedNote>
              )}

              <Title data-half-anchor>{event.name}</Title>
              <SourceLine>
                <span>From {event.sourceName}</span>
                {event.status === 'cancelled' && <StateChip data-kind="cancelled">Cancelled</StateChip>}
                {event.status !== 'cancelled' && event.capacityStatus === 'full' && <StateChip>Full</StateChip>}
              </SourceLine>


              <Facts>
                <Fields>
                  <Field type="button" onClick={() => reveal(when.current)}>
                    <FieldLine className="field-line">
                      <FieldIcon>
                        <Icon name={`time:${timeSlotOf(event) ?? 'morning'}`} size="100%" />
                      </FieldIcon>
                      <span>{formatTimeRange(event)}</span>
                      {!hasEndTime(event) && !event.isAllDay && <EndNote>end time not listed</EndNote>}
                    </FieldLine>
                  </Field>

                  <Field type="button" onClick={() => reveal(where.current)}>
                    <FieldLine className="field-line" style={{ letterSpacing: '0.05em' }}>
                      <FieldIcon>
                        <LocationPin />
                      </FieldIcon>
                      <span>{formatArea(event.neighborhood) || venueText(event).toUpperCase()}</span>
                    </FieldLine>
                  </Field>
                </Fields>

                {event.topics.length > 0 && (
                  <Tags>
                    {(allTagsFor === event.id ? event.topics : event.topics.slice(0, TAG_LIMIT)).map((topic) => {
                      const color = TAG_COLORS[`topic:${topic.key}`] ?? BRAND.muted;
                      return (
                        <TagTab key={topic.key}>
                          <TagLabel style={{ background: `color-mix(in srgb, ${color} 55%, ${BRAND.parchment})` }}>
                            <span>{topic.label}</span>
                          </TagLabel>
                          <TagChip style={{ background: color }}>
                            <Glyph name={`topic:${topic.key}`} emoji={topic.icon} />
                          </TagChip>
                        </TagTab>
                      );
                    })}
                    {event.topics.length > TAG_LIMIT && (
                      <MoreTags
                        type="button"
                        onClick={() => setAllTagsFor(allTagsFor === event.id ? null : event.id)}
                      >
                        {allTagsFor === event.id ? 'Fewer tags' : `+${event.topics.length - TAG_LIMIT} more`}
                      </MoreTags>
                    )}
                  </Tags>
                )}
              </Facts>

              {/* The long form here, the one-liner on the card. Where no full
                  text has been scraped yet, enrichment falls this back to the
                  summary, so the ticket is never empty. */}
              {(event.description || event.summary) && (
                <Description>{event.description || event.summary || ''}</Description>
              )}
              <Perforation $edge="bottom" aria-hidden="true">
                <Holes />
              </Perforation>
            </Body>

            <Stub $torn={scheduled} $live={live === event.id}>
              <Perforation $edge="top" aria-hidden="true">
                <Holes />
              </Perforation>
              <TearPoint aria-hidden="true">
                <Confetti trigger={burst} pieces={28} reach={46} size={5} durationMs={900} />
              </TearPoint>
              <StubHalf>
                {event.roles.length > 0 && (
                  <Intents data-two={event.roles.length > 1}>
                    {event.roles.slice(0, 2).map((role) => (
                      <Glyph key={role.key} name={`role:${role.key}`} emoji={role.icon} />
                    ))}
                  </Intents>
                )}
                <TypeWord>{COMMITMENT_LABEL[event.commitment]}</TypeWord>
              </StubHalf>
              <StubHalf style={{ paddingTop: tw(0.069) }}>
                <StubLabel>FORMAT</StubLabel>
                <StubValue>{event.format === 'virtual' ? 'Online' : formatLabel(event.format)}</StubValue>
                <StubLabel>ENTRY</StubLabel>
                <StubValue>{entryWord(event)}</StubValue>
              </StubHalf>
            </Stub>

            <Rule style={{ marginTop: tw(0.087) }} />

            <Section ref={when}>
              <SectionLabel>When</SectionLabel>
              <SectionHeading>
                {longDate(localDate(event)).full}
                <span>{longDate(localDate(event)).weekday}</span>
              </SectionHeading>
              {event.isAllDay && (
                <SectionNote>
                  This listing gives no start time — it may run all day or across several days. Check the
                  event page.
                </SectionNote>
              )}
              {!event.isAllDay && !hasEndTime(event) && (
                <SectionNote>
                  No end time was published. We've assumed one hour when placing it on your schedule.
                </SectionNote>
              )}
              {event.seriesLabel && <SectionNote>{event.seriesLabel}</SectionNote>}
              {event.prerequisite && <Prereq>{event.prerequisite}</Prereq>}

              {/* A series: every upcoming date, each its own Going and Maybe. */}
              {seriesDates.length > 1 && (
                <Dates aria-label="Dates">
                  {seriesDates.map((date) => {
                    const status = statusOf?.(date.id) ?? null;
                    const current = date.id === event.id;
                    return (
                      <DateRow key={date.id} data-current={current}>
                        <DateLink
                          type="button"
                          aria-current={current ? 'true' : undefined}
                          onClick={() => !current && onOpenEvent(date)}
                        >
                          {shortDate(localDate(date))} · {formatTimeRange(date)}
                        </DateLink>
                        <DateToggle
                          type="button"
                          aria-pressed={status === 'going'}
                          disabled={!canGo(date) && status !== 'going'}
                          onClick={() => onToggleSchedule(date.id)}
                        >
                          Going
                        </DateToggle>
                        <DateToggle type="button" aria-pressed={status === 'maybe'} onClick={() => onToggleSave(date.id)}>
                          Maybe
                        </DateToggle>
                      </DateRow>
                    );
                  })}
                </Dates>
              )}
            </Section>

            <Rule style={{ marginTop: tw(0.09) }} />

            <Section ref={where}>
              <SectionLabel>Where</SectionLabel>
              <SectionHeading>{event.neighborhood ? areaName(event.neighborhood) : venueText(event)}</SectionHeading>
              {event.neighborhood && <Address>{venueText(event)}</Address>}

              {/* Only rendered when the venue geocoded — a borough-only or
                  missing location gets no map rather than a misleading pin.
                  The map takes its own drags, even from a half-up callout. */}
              <MapSlot data-own-swipe>
                <EventMap
                  event={event}
                  before={null}
                  after={null}
                  height={tw(0.476)}
                />
              </MapSlot>
            </Section>

            <PlainActions>
              <PlainAction type="button" onClick={() => onReport(event)}>
                <Glyph name="ui:report" />
                Something wrong with this listing?
              </PlainAction>
              {/* Unavailable for an event on either list: planning to go and
                  not being interested contradict each other. */}
              <PlainAction
                type="button"
                data-danger={hidden || scheduled || saved ? undefined : 'true'}
                aria-disabled={!hidden && (scheduled || saved) ? true : undefined}
                onClick={() => {
                  if (hidden || !(scheduled || saved)) onToggleHidden(event.id);
                }}
              >
                <Glyph name="ui:notInterested" />
                {hidden ? 'Show this event on my Explore page again' : 'I’m not Interested in this event'}
              </PlainAction>
              {!hidden && (scheduled || saved) && (
                <PlainNote>
                  {scheduled
                    ? "You're going to this event. Clear Going to hide it."
                    : 'This event is marked Maybe. Clear Maybe to hide it.'}
                </PlainNote>
              )}
            </PlainActions>
          </Ground>
        </Column>
      </Scroll>

      <Footer data-callout-chrome>
        <Column>
          <Ground>
            <Rule />
            {/* The RSVP block: what you said, and the two buttons. Going never
                opens the registration link (Sam's rule): Register does that. */}
            {(scheduled || saved) && <RsvpLine>{scheduled ? "You're going" : 'You might go'}</RsvpLine>}
            <FootButtons style={canGo(event) || scheduled ? undefined : { gridTemplateColumns: '1fr' }}>
              <FootButton $on={saved} onClick={() => onToggleSave(event.id)} aria-pressed={saved}>
                <Glyph name={saved ? 'ui:saveOn' : 'ui:save'} />
                Maybe
              </FootButton>
              {(canGo(event) || scheduled) && (
                <FootButton
                  $on={scheduled}
                  onClick={() => {
                    if (!scheduled) setBurst((n) => n + 1);
                    onToggleSchedule(event.id);
                  }}
                  aria-pressed={scheduled}
                >
                  <Glyph name={scheduled ? 'ui:scheduleOn' : 'ui:schedule'} />
                  Going
                  <Confetti trigger={burst} />
                </FootButton>
              )}
            </FootButtons>
            {event.status !== 'cancelled' && (
              <Proof>
                {sampleAttendanceText(event.id)}
                <SampleMark>Sample</SampleMark>
              </Proof>
            )}

            <FootLinks>
              {onCopyLink && (
                <GoToSchedule type="button" onClick={() => onCopyLink(event)}>
                  <LinkGlyph />
                  Copy link
                </GoToSchedule>
              )}
              {/* Once Going, the way to the calendar. */}
              {scheduled && (
                <GoToSchedule type="button" onClick={onGoToSchedule}>
                  Go to Saved
                  <Glyph name="ui:arrowUpRight" />
                </GoToSchedule>
              )}
            </FootLinks>

            {/* The one control that leaves the site: Register, else the listing itself. */}
            <Register href={eventUrl(event)} target="_blank" rel="noopener noreferrer">
              {event.registrationUrl ? 'Register on the event page ↗' : `View on ${event.sourceName} ↗`}
            </Register>
            <FootNote>Opens the organizer's own page. Going here doesn't register you.</FootNote>
          </Ground>
        </Column>
      </Footer>
    </Sheet>
  );

  if (sliding) {
    return (
      <HalfSheet
        open={Boolean(current)}
        contentKey={current?.id ?? null}
        onClose={onClose}
        onClosed={() => setRetained(null)}
        measured
        fullOnly={layout === 'sheet'}
        scrim={layout === 'sheet'}
      >
        {sheet}
      </HalfSheet>
    );
  }

  // Beside the list, in the page's own row: no modal, so the page stays live.
  if (layout === 'pane') return sheet;

  // Over the list: it is dimmed and out of reach, and a click anywhere on it
  // closes the callout.
  return (
    <Drawer
      anchor="right"
      open={Boolean(event)}
      onClose={onClose}
      slotProps={{ paper: { sx: { background: SIDEBAR_GROUND, backgroundImage: 'none' } } }}
    >
      {sheet}
    </Drawer>
  );
}
